/* eslint-disable prettier/prettier */
import React, { useState, useRef } from 'react';

import moment from 'moment';

import PeriodSelect from 'components/PeriodSelect';
import Section from 'components/Section';
import CustomSelect, { RenderProps as CustomSelectRenderProps } from 'components/Selects/CustomSelect';
import SearchSelect, { RenderProps as SearchSelectRenderProps } from 'components/Selects/SearchSelct';
import useIsMobile from 'hooks/useIsMobile';
import useStoreTranslation from 'hooks/useStoreTranslation';
import { CostCenter } from 'models/costCenter';
import { CostCenterReportProducts, CostCenterReportRequest } from 'models/costCenterReport';
import { DefaultCostCenterId } from 'screens/components/TravelReport/TravelReportFilter/constants/cost-centers';

import { ReportFiltersContainer } from '../styles';
import OptionsItems from './components/OptionsItems';
import { Button } from './styles';

interface Props {
  loading: boolean;
  costCenterList: CostCenter[];
  fetchReportData: (filters: CostCenterReportRequest, type: string) => Promise<any>;
  onSetPeriodText: (period?: string) => void;
  isSubmit: boolean;
}

const CostCenterReportForm = ({ loading, costCenterList, fetchReportData, onSetPeriodText, isSubmit }: Props) => {
  const { t } = useStoreTranslation();
  const isMobile = useIsMobile();

  const [costCenterValue, setSearchText] = useState('');
  const [selectedProduct, setSelectedProduct] = useState<CostCenterReportProducts>();
  const [selectedCostCenter, setSelectedCostCenter] = useState<CostCenter>();
  const [filterStartDate, setFilterStartDate] = useState<string>();
  const [filterEndDate, setFilterEndDate] = useState<string>();
  const productRef = useRef(null);
  const costCenterRef = useRef(null);
  const productList = [
    { label: t('costCenter.report.filterForm.products.all'), value: CostCenterReportProducts.All },
    { label: t('costCenter.report.filterForm.products.flight'), value: CostCenterReportProducts.Air },
    { label: t('costCenter.report.filterForm.products.vehicle'), value: CostCenterReportProducts.Vehicle },
    { label: t('costCenter.report.filterForm.products.hotel'), value: CostCenterReportProducts.Hotel },
  ];
  const [costCenters, setCostCenters] = useState(costCenterList);

  const [labelReport, setLabelReport] = useState('');
  const [selectedProductText, setProductLabel] = useState('');

  const handleSelectProduct = (name: string) => {
    const product = productList.find((g) => g.value === name);
    setProductLabel(product?.label ?? '');
    setSelectedProduct(product?.value as CostCenterReportProducts);
  };

  const handleChangeCostCenter = (e: React.FormEvent<HTMLInputElement>) => {
    const inputedValue = e.currentTarget.value;
    setSearchText(inputedValue);

    if (!inputedValue) {
      setCostCenters(costCenterList);
      setSelectedCostCenter(undefined);
    } else {
      setCostCenters(costCenterList.filter((c) => c.name?.toLowerCase().includes(inputedValue.toLowerCase()) || c.code?.toLowerCase().includes(inputedValue.toLowerCase())));
    }
  };

  const handleSelectCostCenter = (id: string) => {
    const costCenter = costCenters.find((costCenter) => costCenter.id === id);
    setSearchText(costCenter?.name ?? costCenter?.code ?? '');
    setSelectedCostCenter(costCenter);
  };

  const onSeeResults = (type: string) => {
    const data: CostCenterReportRequest = {
      initDate: filterStartDate ?? '',
      endDate: filterEndDate ?? '',
      tripType: selectedProduct ?? CostCenterReportProducts.All,
      costCenterId: selectedCostCenter
        ? selectedCostCenter.id ?? DefaultCostCenterId
        : DefaultCostCenterId,
    };

    onSetPeriodText(labelReport);

    fetchReportData(data, type);
  };

  const isValidCostCenter = () => {
    if (costCenterValue || selectedCostCenter) {
      const condition = costCenterValue.toLowerCase() === selectedCostCenter?.name?.toLowerCase();

      if (!condition) return t('costCenter.report.filterForm.costCenterInvalid');
    }
  };

  const changeDateFilters = (start: string, end: string, value: string) => {
    setFilterEndDate(end);
    setFilterStartDate(start);

    let label = '';
    if (value === 'date') {
      label = `${moment(start).format('DD MMM')} - ${moment(end).format('DD MMM')}`;
    }
    if (value === 'thirtyDays') {
      label = t('periodSelect.options.lastDays');
    }
    if (value === 'previous') {
      label = t('periodSelect.options.previousMonth', {
        month: moment().subtract(1, 'months').endOf('month').format('MMMM'),
      });
    }
    if (value === 'current') {
      label = t('periodSelect.options.currentMonth', { month: moment().format('MMMM') });
    }

    setLabelReport(label);
  };

  return (
    <ReportFiltersContainer>
      <Section title={t('costCenter.report.filterForm.title')} joinContent={false}>
        <PeriodSelect label={t('costCenter.report.filterForm.selectPeriod')} handleChangeDate={changeDateFilters} />
        <CustomSelect
          name="product"
          onChange={handleSelectProduct}
          label={t('costCenter.report.filterForm.selectProduct')}
          placeHolder={t('costCenter.report.filterForm.selectProductPlaceholder')}
          selectRef={productRef}
          value={selectedProductText}
        >
          {({ ...selectProps }: CustomSelectRenderProps) => (
            <OptionsItems {...selectProps} selectRef={productRef} products={productList} />
          )}
        </CustomSelect>
        <SearchSelect
          name="costCenter"
          error={isValidCostCenter()}
          onChange={handleChangeCostCenter}
          onChildrenChange={handleSelectCostCenter}
          label={t('costCenter.report.filterForm.selectCostCenter')}
          placeHolder={t('costCenter.report.filterForm.selectCostCenterPlaceholder')}
          selectRef={costCenterRef}
          value={costCenterValue}
        >
          {({ ...selectProps }: SearchSelectRenderProps) => (
            <OptionsItems {...selectProps} selectRef={costCenterRef} costCenters={costCenters} />
          )}
        </SearchSelect>
      </Section>
      <Section>
        <Button
          invertedColor
          disabled={isSubmit || !filterStartDate || !filterEndDate}
          onClick={() => {
            onSeeResults('email');
          }}
          value={t('costCenter.report.filterForm.sendToEmail')}
          loading={isSubmit}
        />
        {!isMobile && (
          <Button
            invertedColor
            disabled={isSubmit || !filterStartDate || !filterEndDate}
            onClick={() => {
              onSeeResults('screen');
            }}
            value={t('costCenter.report.filterForm.search')}
            loading={isSubmit}
          />
        )}
      </Section>
    </ReportFiltersContainer>
  );
};

export default CostCenterReportForm;
